import r from '../Utils/fetch';

const w: any = window;
const API_URL = w.API_MCP_PUBLIC_URL;

// =============================================================================
// TYPES
// =============================================================================

export interface SchemaSnapshot {
	id: string;
	project_id: string;
	dw_type: string;
	dataset_count: number;
	table_count: number;
	column_count: number;
	is_current: boolean;
	extracted_at: string;
	created_at: string;
}

export interface TableMetadata {
	id: string;
	project_id: string;
	dataset_name: string;
	table_name: string;
	description_fr?: string;
	description_en?: string;
	business_domain?: string;
	grain?: string;
	is_recommended: boolean;
	is_hidden: boolean;
	columns?: ColumnMetadata[];
}

export interface ColumnMetadata {
	id: string;
	column_name: string;
	custom_description_fr?: string;
	custom_description_en?: string;
	semantic_type?: string;
	is_pii: boolean;
	is_hidden: boolean;
}

// Semantic codes structure (required/optional)
export interface SemanticCodesData {
	required: string[];
	optional: string[];
}

// Output structure (dimensions/metrics avec alias)
export interface UseCaseOutput {
	dimensions?: { code: string; alias: string }[];
	metrics?: { code: string; alias: string }[];
}

// Use Case (nouveau format v2)
export interface UseCase {
	id: string;
	slug: string; // Ex: uc01_ad_centric_uniformization
	version: number;
	name: string;
	description?: string;
	category?: string;
	tags?: string[];
	semantic_codes: SemanticCodesData;
	required_skus?: string[]; // SKUs requis (vide = global)
	depends_on?: string[]; // Dépendances vers d'autres use cases
	output?: UseCaseOutput;
	sql_template?: string;
	prompt?: string;
	difficulty?: string;
	scope: 'member' | 'project' | 'global';
	owner_id?: string;
	project_folder_id?: string;
	status: 'draft' | 'published';
	is_featured: boolean;
	source_file?: string;
	source_commit?: string;
	created_at: string;
	updated_at: string;
}

// Alias pour compatibilité avec l'ancien code
export type Template = UseCase;

export interface SyncResult {
	total_files: number;
	created: number;
	updated: number;
	errors: number;
	error_list?: string[];
	duration: string;
}

export interface SchemaSyncResponse {
	status: 'started' | 'completed' | 'error';
	project_id: string;
	message: string;
}

interface ApiResponse<T> {
	data: T;
	error?: string;
}

// =============================================================================
// SCHEMA
// =============================================================================

export const getSchemaSnapshots = (
	projectFolderId: string
): Promise<ApiResponse<SchemaSnapshot[]>> =>
	r({
		url: `${API_URL}/admin/projects/${projectFolderId}/schema/snapshots`,
		method: 'GET',
	});

export const getCurrentSchema = (
	projectFolderId: string
): Promise<ApiResponse<SchemaSnapshot>> =>
	r({
		url: `${API_URL}/admin/projects/${projectFolderId}/schema`,
		method: 'GET',
	});

export const syncSchema = (
	projectFolderId: string
): Promise<SchemaSyncResponse> =>
	r({
		url: `${API_URL}/admin/projects/${projectFolderId}/schema/sync`,
		method: 'POST',
	});

// =============================================================================
// TABLES
// =============================================================================

export const getTables = (
	projectFolderId: string,
	filters?: { dataset?: string; domain?: string; recommended?: boolean }
): Promise<ApiResponse<TableMetadata[]>> =>
	r({
		url: `${API_URL}/admin/projects/${projectFolderId}/tables`,
		method: 'GET',
		data: filters,
	});

export const getTable = (
	projectFolderId: string,
	dataset: string,
	table: string
): Promise<ApiResponse<TableMetadata>> =>
	r({
		url: `${API_URL}/admin/projects/${projectFolderId}/tables/${dataset}/${table}`,
		method: 'GET',
	});

export const updateTable = (
	projectFolderId: string,
	dataset: string,
	table: string,
	data: Partial<TableMetadata>
): Promise<ApiResponse<TableMetadata>> =>
	r({
		url: `${API_URL}/admin/projects/${projectFolderId}/tables/${dataset}/${table}`,
		method: 'PUT',
		data,
	});

export const updateColumn = (
	projectFolderId: string,
	dataset: string,
	table: string,
	column: string,
	data: Partial<ColumnMetadata>
): Promise<ApiResponse<ColumnMetadata>> =>
	r({
		url: `${API_URL}/admin/projects/${projectFolderId}/tables/${dataset}/${table}/columns/${column}`,
		method: 'PUT',
		data,
	});

// =============================================================================
// USE CASES / TEMPLATES
// =============================================================================

export const getTemplates = (): Promise<ApiResponse<Template[]>> =>
	r({
		url: `${API_URL}/admin/use-cases`,
		method: 'GET',
	});

export const getTemplate = (code: string): Promise<ApiResponse<Template>> =>
	r({
		url: `${API_URL}/admin/use-cases/${code}`,
		method: 'GET',
	});

export const createTemplate = (
	data: Partial<Template>
): Promise<ApiResponse<Template>> =>
	r({
		url: `${API_URL}/admin/use-cases`,
		method: 'POST',
		data,
	});

export const updateTemplate = (
	code: string,
	data: Partial<Template>
): Promise<ApiResponse<Template>> =>
	r({
		url: `${API_URL}/admin/use-cases/${code}`,
		method: 'PUT',
		data,
	});

export const deleteTemplate = (code: string): Promise<ApiResponse<void>> =>
	r({
		url: `${API_URL}/admin/use-cases/${code}`,
		method: 'DELETE',
	});

export const syncUseCases = (): Promise<SyncResult> =>
	r({
		url: `${API_URL}/admin/use-cases/sync`,
		method: 'POST',
	});

// =============================================================================
// LLM ENRICHMENTS (qm_llm_tables, qm_llm_fields)
// =============================================================================

export interface LLMTableSummary {
	id: string;
	sku: string;
	table_id: string;
	dw_table_name?: string;
	name: string;
	description?: string;
	business_domain?: string;
	semantic_code?: string;
	is_prebuild: boolean;
	project_folder_id?: string;
	account_id?: string;
	fields_count: number;
	is_active: boolean;
}

export interface LLMFieldSummary {
	id: string;
	field_id: string;
	type: string;
	semantic_code?: string;
	description?: string;
	business_name?: string;
	is_metric: boolean;
	is_pii: boolean;
	is_hidden: boolean;
	display_order: number;
}

export interface LLMTableDetail extends LLMTableSummary {
	purpose?: string;
	semantic_category?: string;
	fields: LLMFieldSummary[];
}

export interface LLMTableFilters {
	sku?: string;
	project_folder_id?: string;
	account_id?: string;
	only_prebuilds?: boolean;
}

export interface UpdateLLMTableInput {
	name?: string;
	description?: string;
	purpose?: string;
	business_domain?: string;
	semantic_code?: string;
	semantic_category?: string;
	is_active?: boolean;
}

export interface UpdateLLMFieldInput {
	field_id: string;
	semantic_code?: string;
	description?: string;
	purpose?: string;
	business_name?: string;
	format_hint?: string;
	is_metric?: boolean;
	is_pii?: boolean;
	is_hidden?: boolean;
	display_order?: number;
}

// Liste des tables LLM avec filtres
export const getLLMTables = (
	filters?: LLMTableFilters
): Promise<{ tables: LLMTableSummary[]; count: number }> =>
	r({
		url: `${API_URL}/admin/llm/tables`,
		method: 'GET',
		data: filters,
	});

// Détail d'une table LLM avec ses fields
export const getLLMTable = (id: string): Promise<LLMTableDetail> =>
	r({
		url: `${API_URL}/admin/llm/tables/${id}`,
		method: 'GET',
	});

// MAJ d'une table LLM
export const updateLLMTable = (
	id: string,
	data: UpdateLLMTableInput
): Promise<{ success: boolean; message: string }> =>
	r({
		url: `${API_URL}/admin/llm/tables/${id}`,
		method: 'PUT',
		data,
	});

// Suppression d'une table LLM
export const deleteLLMTable = (
	id: string
): Promise<{ success: boolean; message: string }> =>
	r({
		url: `${API_URL}/admin/llm/tables/${id}`,
		method: 'DELETE',
	});

// Liste des fields d'une table
export const getLLMFields = (
	tableId: string
): Promise<{ fields: LLMFieldSummary[]; count: number }> =>
	r({
		url: `${API_URL}/admin/llm/tables/${tableId}/fields`,
		method: 'GET',
	});

// MAJ d'un field
export const updateLLMField = (
	tableId: string,
	fieldId: string,
	data: Partial<UpdateLLMFieldInput>
): Promise<{ success: boolean; message: string }> =>
	r({
		url: `${API_URL}/admin/llm/tables/${tableId}/fields/${fieldId}`,
		method: 'PUT',
		data,
	});

// Batch update des fields
export const batchUpdateLLMFields = (
	tableId: string,
	fields: UpdateLLMFieldInput[]
): Promise<{ success: boolean; message: string; count: number }> =>
	r({
		url: `${API_URL}/admin/llm/tables/${tableId}/fields`,
		method: 'PATCH',
		data: { fields },
	});

// Créer un override client
export const createLLMOverride = (
	tableId: string,
	projectFolderId: string,
	accountId: string
): Promise<{ success: boolean; message: string; override: LLMTableDetail }> =>
	r({
		url: `${API_URL}/admin/llm/tables/${tableId}/override`,
		method: 'POST',
		data: { project_folder_id: projectFolderId, account_id: accountId },
	});

// Liste des semantic codes (pour autocomplete)
export const getLLMSemanticCodes = (): Promise<{ codes: string[]; count: number }> =>
	r({
		url: `${API_URL}/admin/llm/semantic-codes`,
		method: 'GET',
	});

// =============================================================================
// SEMANTIC CODES (qm_llm_semantic_codes - glossaire central)
// =============================================================================

export interface SemanticCode {
	id: string;
	code: string;
	type: string; // entity, dimension, measure, metric
	name: string;
	definition?: string;
	formula?: string;
	unit?: string;
	synonyms?: string[];
	aggregation?: string;
	category?: string;
	connector_type?: string;
	created_at: string;
	updated_at: string;
}

export interface SemanticCodeFilters {
	type?: string;
	category?: string;
	connector_type?: string;
	limit?: number;
	offset?: number;
}

export interface CreateSemanticCodeInput {
	code: string;
	type: string;
	name: string;
	definition?: string;
	formula?: string;
	unit?: string;
	synonyms?: string[];
	aggregation?: string;
	category?: string;
	connector_type?: string;
}

export interface UpdateSemanticCodeInput {
	name?: string;
	definition?: string;
	formula?: string;
	unit?: string;
	synonyms?: string[];
	aggregation?: string;
	category?: string;
	connector_type?: string;
}

// Liste des semantic codes avec filtres
export const getSemanticCodesList = (
	filters?: SemanticCodeFilters
): Promise<{ codes: SemanticCode[]; count: number }> =>
	r({
		url: `${API_URL}/admin/llm/semantic-codes/list`,
		method: 'GET',
		data: filters,
	});

// Détail d'un semantic code
export const getSemanticCode = (code: string): Promise<SemanticCode> =>
	r({
		url: `${API_URL}/admin/llm/semantic-codes/${code}`,
		method: 'GET',
	});

// Créer un semantic code
export const createSemanticCode = (
	data: CreateSemanticCodeInput
): Promise<{ success: boolean; message: string; code: SemanticCode }> =>
	r({
		url: `${API_URL}/admin/llm/semantic-codes`,
		method: 'POST',
		data,
	});

// Mettre à jour un semantic code
export const updateSemanticCode = (
	code: string,
	data: UpdateSemanticCodeInput
): Promise<{ success: boolean; message: string }> =>
	r({
		url: `${API_URL}/admin/llm/semantic-codes/${code}`,
		method: 'PUT',
		data,
	});

// Supprimer un semantic code
export const deleteSemanticCode = (
	code: string
): Promise<{ success: boolean; message: string }> =>
	r({
		url: `${API_URL}/admin/llm/semantic-codes/${code}`,
		method: 'DELETE',
	});

// Résultat du sync LLM
export interface LLMSyncResult {
	sku: string;
	tables_created: number;
	tables_updated: number;
	fields_created: number;
	fields_updated: number;
	// Templates de push d'un connecteur reverse, volontairement non enregistrés
	// (ils ne produisent aucune table). Optionnel : absent des réponses des
	// instances pas encore à jour.
	pushes_skipped?: number;
	duration: number; // nanoseconds
	error?: string;
}

// Sync des prebuilds depuis GitLab vers PostgreSQL
export const syncLLM = (
	sku: string
): Promise<{ success: boolean; message: string; result: LLMSyncResult }> =>
	r({
		url: `${API_URL}/admin/llm/sync/${sku}`,
		method: 'POST',
	});

// Export CSV des fields d'un SKU (téléchargement direct)
export const exportFieldsCSV = async (sku: string): Promise<void> => {
	const token = localStorage.getItem('token');
	const response = await fetch(`${API_URL}/admin/llm/fields/export?sku=${sku}`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({ error: 'Export failed' }));
		throw new Error(error.error || 'Export failed');
	}

	// Télécharger le fichier
	const blob = await response.blob();
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = `${sku}_fields.csv`;
	document.body.appendChild(a);
	a.click();
	window.URL.revokeObjectURL(url);
	document.body.removeChild(a);
};

// Import CSV des fields
export interface ImportCSVResult {
	updated: number;
	skipped: number;
	errors?: string[];
}

export const importFieldsCSV = async (file: File): Promise<ImportCSVResult> => {
	const token = localStorage.getItem('token');
	const csvData = await file.text();

	const response = await fetch(`${API_URL}/admin/llm/fields/import`, {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'text/csv',
		},
		body: csvData,
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({ error: 'Import failed' }));
		throw new Error(error.error || 'Import failed');
	}

	const data = await response.json();
	return data.result;
};

// =============================================================================
// CONNECTORS (depuis API Connector)
// =============================================================================

const API_CONNECTORS_V2_URL = w.API_CONNECTORS_CONFIG_URL;

export interface Connector {
	id: number;
	sku: string;
	name: string;
	version: string;
	processVersion: string;
}

// Liste des connecteurs avec leurs SKUs
export const getConnectors = (): Promise<Connector[]> =>
	r({
		url: `${API_CONNECTORS_V2_URL}/v1/admin/connectors`,
		method: 'GET',
	});

// Liste des SKUs uniques (extrait depuis les connecteurs v2 uniquement)
export const getConnectorSKUs = async (): Promise<string[]> => {
	const connectors = await getConnectors();
	// Filtre uniquement les connecteurs v2 (processVersion commence par "v2" ou "2.")
	const v2Connectors = connectors.filter((c) => /^v?2\./.test(c.processVersion || ''));
	const skus = [...new Set(v2Connectors.map((c) => c.sku))];
	return skus.sort();
};

// =============================================================================
// QDRANT SYNC (Semantic Codes → Vector DB)
// =============================================================================

export interface QdrantSyncResult {
	success: boolean;
	total_codes: number;
	indexed: number;
	errors: number;
	error_details?: string;
}

export interface QdrantStats {
	collection: string;
	total_points: number;
}

export interface QdrantSearchResult {
	id: string;
	score: number;
	code: string;
	type: string;
	name: string;
	definition?: string;
	formula?: string;
	synonyms?: string[];
}

// Sync tous les semantic codes vers Qdrant
export const syncSemanticCodesToQdrant = (): Promise<QdrantSyncResult> =>
	r({
		url: `${API_URL}/admin/qdrant/semantic-codes/sync`,
		method: 'POST',
	});

// Stats de la collection Qdrant
export const getQdrantSemanticCodesStats = (): Promise<QdrantStats> =>
	r({
		url: `${API_URL}/admin/qdrant/semantic-codes/stats`,
		method: 'GET',
	});

// Recherche sémantique dans Qdrant
export const searchSemanticCodesQdrant = (
	query: string,
	limit?: number,
	filters?: { type?: string; category?: string; connector_type?: string }
): Promise<{ query: string; results: QdrantSearchResult[]; count: number }> =>
	r({
		url: `${API_URL}/admin/qdrant/semantic-codes/search`,
		method: 'POST',
		data: { query, limit: limit || 10, ...filters },
	});

// =============================================================================
// CONNECTOR REQUESTS (requests actives d'un connector_account)
// =============================================================================

export interface ConnectorRequest {
	id: string;
	sku: string;
	sku_parent: string;
	project_folder_id: string;
	connector_account_id: string;
	request_id: string;
	request_name: string;
	request_type: 'prebuild' | 'custom';
	status: 'active' | 'inactive';
	created_at: string;
	updated_at: string;
}

// Liste des requests d'un connector_account
export const getConnectorRequests = (
	projectFolderId: string,
	sku: string,
	accountId: string
): Promise<{ requests: ConnectorRequest[]; count: number }> =>
	r({
		url: `${API_URL}/admin/connector-accounts/${projectFolderId}/${sku}/${accountId}/requests`,
		method: 'GET',
	});

// =============================================================================
// USE CASES QDRANT (Vector DB pour RAG)
// =============================================================================

export interface UseCaseSyncResult {
	success: boolean;
	total_use_cases: number;
	indexed: number;
	errors: number;
	error_details?: string;
}

export interface UseCaseQdrantStats {
	total_points: number;
}

export interface UseCaseSearchResult {
	id: string;
	score: number;
	code: string;
	name: string;
	description?: string;
	category?: string;
	tags?: string[];
	semantic_codes_required?: string[];
	semantic_codes_optional?: string[];
	required_skus?: string[];
	difficulty?: string;
	is_featured?: string;
}

// Sync tous les use cases vers Qdrant
export const syncUseCasesToQdrant = (): Promise<UseCaseSyncResult> =>
	r({
		url: `${API_URL}/admin/qdrant/use-cases/sync`,
		method: 'POST',
	});

// Stats de la collection Qdrant use_cases
export const getQdrantUseCasesStats = (): Promise<UseCaseQdrantStats> =>
	r({
		url: `${API_URL}/admin/qdrant/use-cases/stats`,
		method: 'GET',
	});

// Recherche sémantique de use cases dans Qdrant
export const searchUseCasesQdrant = (
	query: string,
	limit?: number,
	filters?: { category?: string; difficulty?: string }
): Promise<{ query: string; results: UseCaseSearchResult[]; count: number }> =>
	r({
		url: `${API_URL}/admin/qdrant/use-cases/search`,
		method: 'POST',
		data: { query, limit: limit || 5, ...filters },
	});

// Index un use case spécifique dans Qdrant
export const indexUseCaseToQdrant = (
	id: string
): Promise<{ success: boolean; message: string }> =>
	r({
		url: `${API_URL}/admin/qdrant/use-cases/${id}/index`,
		method: 'POST',
	});

// =============================================================================
// SCHEMA QDRANT (Tables indexées pour RAG)
// =============================================================================

export interface SchemaTableResult {
	dataset: string;
	name: string;
	full_name: string;
	description: string;
	columns: { name: string; type: string; description?: string }[];
	score: number;
}

export interface SchemaSyncConnectorResult {
	sku: string;
	account_id: string;
	success: boolean;
	tables_indexed?: number;
	duration_ms?: number;
	error?: string;
}

export interface SchemaSyncProjectResult {
	success: boolean;
	project_id: string;
	connectors: SchemaSyncConnectorResult[];
	total_indexed: number;
	message?: string;
}

// Sync un projet entier vers Qdrant (tous les connecteurs actifs)
export const syncProjectSchema = (
	projectFolderId: string
): Promise<SchemaSyncProjectResult> =>
	r({
		url: `${API_URL}/admin/schema/sync-project/${projectFolderId}`,
		method: 'POST',
	});

// Sync un connector_account vers Qdrant
export const syncConnectorSchema = (
	projectFolderId: string,
	sku: string,
	accountId: string
): Promise<SchemaSyncConnectorResult> =>
	r({
		url: `${API_URL}/admin/schema/sync-connector/${projectFolderId}/${sku}/${accountId}`,
		method: 'POST',
	});

// Recherche dans le schema Qdrant d'un projet
export const searchSchemaQdrant = (
	projectId: string,
	query: string,
	limit?: number
): Promise<{
	success: boolean;
	project_id: string;
	query: string;
	tables: SchemaTableResult[];
	total_found: number;
}> =>
	r({
		url: `${API_URL}/admin/schema/search`,
		method: 'POST',
		data: { project_id: projectId, query, limit: limit || 10 },
	});

// =============================================================================
// SYNC ALL SCHEMAS (Tous les projets vers Qdrant)
// =============================================================================

export interface SyncAllSchemasResult {
	success: boolean;
	message: string;
	projects_synced?: number;
	total_tables?: number;
}

// Sync tous les schemas de tous les projets vers Qdrant
export const syncAllSchemas = (): Promise<SyncAllSchemasResult> =>
	r({
		url: `${API_URL}/admin/schema/sync-all`,
		method: 'POST',
	});

// =============================================================================
// MCP CONVERSATIONS (Historique + Feedback)
// =============================================================================

export interface MCPSession {
	id: string;
	user_id: number;
	user_email?: string;
	user_name?: string;
	project_folder_id?: string;
	client_type?: string;
	message_count: number;
	tool_call_count: number;
	total_duration_ms: number;
	started_at: string;
	last_activity_at: string;
	feedback_count: number;
}

export interface MCPMessage {
	id: string;
	request_id?: string;
	method: string;
	tool_name?: string;
	tool_arguments?: any;
	success: boolean;
	result_text?: string;
	result_size?: number;
	error_message?: string;
	duration_ms?: number;
	created_at: string;
}

export interface MCPFeedback {
	id: string;
	session_id?: string;
	message_id?: string;
	user_id: number;
	user_email?: string;
	project_folder_id?: string;
	rating: 'positive' | 'negative' | 'neutral';
	category?: string;
	comment?: string;
	original_question?: string;
	tool_name?: string;
	created_at: string;
}

export interface MCPSessionDetail extends MCPSession {
	messages: MCPMessage[];
	feedback: MCPFeedback[];
}

export interface MCPToolStat {
	tool_name: string;
	call_count: number;
	success_rate: number;
	avg_duration_ms: number;
}

export interface MCPDailyStat {
	date: string;
	sessions: number;
	messages: number;
	unique_users: number;
	positive_feedback: number;
	negative_feedback: number;
}

export interface MCPStats {
	total_sessions: number;
	total_messages: number;
	total_feedback: number;
	positive_feedback: number;
	negative_feedback: number;
	unique_users: number;
	avg_messages_per_session: number;
	top_tools: MCPToolStat[];
	daily_stats: MCPDailyStat[];
}

export interface MCPSessionFilters {
	user_id?: number;
	project_folder_id?: string;
	client_type?: string;
	since?: string;
	limit?: number;
	offset?: number;
}

export interface MCPFeedbackFilters {
	rating?: string;
	category?: string;
	user_id?: number;
	since?: string;
	limit?: number;
	offset?: number;
}

// Liste des sessions MCP
export const getMCPSessions = (
	filters?: MCPSessionFilters
): Promise<{ sessions: MCPSession[]; total: number; limit: number; offset: number }> =>
	r({
		url: `${API_URL}/admin/mcp/sessions`,
		method: 'GET',
		data: filters,
	});

// Détail d'une session MCP
export const getMCPSession = (id: string): Promise<MCPSessionDetail> =>
	r({
		url: `${API_URL}/admin/mcp/sessions/${id}`,
		method: 'GET',
	});

// Liste des feedbacks MCP
export const getMCPFeedback = (
	filters?: MCPFeedbackFilters
): Promise<{ feedback: MCPFeedback[]; total: number; limit: number; offset: number }> =>
	r({
		url: `${API_URL}/admin/mcp/feedback`,
		method: 'GET',
		data: filters,
	});

// Stats MCP globales (avec filtres optionnels)
export const getMCPStats = (
	filters?: Pick<MCPSessionFilters, 'user_id' | 'project_folder_id'>
): Promise<MCPStats> =>
	r({
		url: `${API_URL}/admin/mcp/stats`,
		method: 'GET',
		data: filters,
	});

// =============================================================================
// MCP TOOL TESTER
// =============================================================================

export interface MCPTool {
	name: string;
	description: string;
	inputSchema: {
		type: string;
		properties: Record<string, {
			type: string;
			description?: string;
			enum?: string[];
		}>;
		required?: string[];
	};
	annotations?: {
		title?: string;
		readOnlyHint?: boolean;
		destructiveHint?: boolean;
	};
}

export interface MCPToolCallResult {
	success: boolean;
	result?: string;
	error?: string;
}

// Liste des tools MCP disponibles
export const getMCPTools = (): Promise<{ tools: MCPTool[]; count: number }> =>
	r({
		url: `${API_URL}/admin/mcp/tools`,
		method: 'GET',
	});

// =============================================================================
// MCP ANALYTICS
// =============================================================================

export interface MCPAnalysis {
	id: string;
	session_id?: string;
	user_id?: number;
	project_folder_id?: string;
	scope: string;
	status: string;
	result?: {
		summary: string;
		tools_used: string[];
		positive_points: string[];
		improvements: string[];
		recommendations: string[];
		errors_summary: string;
		score: number;
	};
	result_raw?: string;
	model?: string;
	input_tokens: number;
	output_tokens: number;
	total_tokens: number;
	duration_ms: number;
	error_message?: string;
	created_at: string;
	updated_at: string;
}

// Lancer une analyse LLM sur une session
export const analyzeSession = (sessionId: string): Promise<MCPAnalysis> =>
	r({
		url: `${API_URL}/admin/mcp/sessions/${sessionId}/analyze`,
		method: 'POST',
	});

// Récupérer la dernière analyse d'une session
export const getSessionAnalysis = (sessionId: string): Promise<MCPAnalysis> =>
	r({
		url: `${API_URL}/admin/mcp/sessions/${sessionId}/analysis`,
		method: 'GET',
	});

export interface MCPAnalysisWithUser extends MCPAnalysis {
	user_email?: string;
}

export interface MCPAnalysisFilters {
	scope?: string;
	status?: string;
	user_id?: number;
	since?: string;
	until?: string;
	limit?: number;
	offset?: number;
}

export interface MetaAnalysisResult {
	content: string;
	model: string;
	total_tokens: number;
}

// Lancer une méta-analyse sur les analyses filtrées
export const metaAnalyze = (
	filters?: Pick<MCPAnalysisFilters, 'scope' | 'since' | 'until' | 'limit'>
): Promise<MetaAnalysisResult> =>
	r({
		url: `${API_URL}/admin/mcp/analyses/meta`,
		method: 'POST',
		data: filters,
	});

// Sauvegarder une méta-analyse en tant qu'analyse globale
export const saveMetaAnalysis = (
	resultRaw: string,
	model: string,
	totalTokens: number
): Promise<MCPAnalysis> =>
	r({
		url: `${API_URL}/admin/mcp/analyses/save`,
		method: 'POST',
		data: {
			result_raw: resultRaw,
			model,
			total_tokens: totalTokens,
		},
	});

// Liste des analyses avec pagination et filtres
export const listAnalyses = (
	filters?: MCPAnalysisFilters
): Promise<{ analyses: MCPAnalysisWithUser[]; total: number; limit: number; offset: number }> =>
	r({
		url: `${API_URL}/admin/mcp/analyses`,
		method: 'GET',
		data: filters,
	});

// Appelle un tool MCP
export const callMCPTool = (
	toolName: string,
	args: Record<string, any>,
	projectId?: string
): Promise<MCPToolCallResult> =>
	r({
		url: `${API_URL}/admin/mcp/tools/call`,
		method: 'POST',
		data: {
			tool_name: toolName,
			arguments: args,
			project_id: projectId,
		},
	});
